/**
 * Influence Analysis Resource
 */

import type { ZapSEA } from '../client';
import { 
  InfluencePathRequest,
  JobResponse 
} from '../types';

export class InfluenceResource {
  constructor(private client: ZapSEA) {}

  /**
   * Find influence path between entities
   * 
   * @example
   * ```typescript
   * const job = await client.influence.findPath({
   *   sourceEntity: 'Congress',
   *   targetEntity: 'Tech Industry',
   *   maxPathLength: 5,
   *   minInfluenceThreshold: 0.3
   * });
   * 
   * console.log(`Job submitted: ${job.jobId}`);
   * ```
   */
  async findPath(request: InfluencePathRequest): Promise<JobResponse> {
    this._validateInfluencePathRequest(request);
    return this.client.request<JobResponse>('POST', '/v2/influence/path', request);
  }

  /**
   * Get influence network for an entity
   * 
   * @example
   * ```typescript
   * const job = await client.influence.getNetwork({
   *   entity: 'Federal Reserve',
   *   depth: 2,
   *   minInfluenceThreshold: 0.2
   * });
   * ```
   */
  async getNetwork(request: {
    entity: string;
    depth?: number;
    minInfluenceThreshold?: number;
    customParameters?: Record<string, any>;
  }): Promise<JobResponse> {
    this._validateNetworkRequest(request);
    return this.client.request<JobResponse>('POST', '/v2/influence/network', request);
  }

  /**
   * Get influence path result by job ID
   * 
   * @example
   * ```typescript
   * const result = await client.influence.getPathResult('job_abc123');
   * console.log(`Path length: ${result.pathLength}`);
   * ```
   */
  async getPathResult(jobId: string): Promise<any> {
    if (!jobId) {
      throw new Error('Job ID is required');
    }
    return this.client.request('GET', `/v2/influence/results/${jobId}`);
  }

  /**
   * Get top influencers in a domain
   * 
   * @example
   * ```typescript
   * const influencers = await client.influence.getTopInfluencers({
   *   domain: 'technology_policy',
   *   limit: 10
   * });
   * ```
   */
  async getTopInfluencers(request: {
    domain: string;
    limit?: number;
    timeframe?: string;
  }): Promise<any> {
    this._validateTopInfluencersRequest(request);
    return this.client.request('GET', '/v2/influence/top-influencers', request);
  }

  /**
   * Validate influence path request
   */
  private _validateInfluencePathRequest(request: InfluencePathRequest): void {
    if (!request.sourceEntity || typeof request.sourceEntity !== 'string') {
      throw new Error('Source entity is required and must be a string');
    }

    if (!request.targetEntity || typeof request.targetEntity !== 'string') {
      throw new Error('Target entity is required and must be a string');
    }

    if (request.sourceEntity.trim().length < 2) {
      throw new Error('Source entity must be at least 2 characters long');
    }

    if (request.targetEntity.trim().length < 2) {
      throw new Error('Target entity must be at least 2 characters long');
    }

    if (request.sourceEntity === request.targetEntity) {
      throw new Error('Source and target entities must be different');
    }

    if (request.maxPathLength !== undefined) {
      if (typeof request.maxPathLength !== 'number' || request.maxPathLength < 1 || request.maxPathLength > 10) {
        throw new Error('Max path length must be a number between 1 and 10');
      }
    }

    if (request.minInfluenceThreshold !== undefined) {
      if (typeof request.minInfluenceThreshold !== 'number' || 
          request.minInfluenceThreshold < 0 || 
          request.minInfluenceThreshold > 1) {
        throw new Error('Min influence threshold must be a number between 0 and 1');
      }
    }
  }

  /**
   * Validate network request
   */
  private _validateNetworkRequest(request: {
    entity: string;
    depth?: number;
    minInfluenceThreshold?: number;
  }): void {
    if (!request.entity || typeof request.entity !== 'string') {
      throw new Error('Entity is required and must be a string');
    }

    if (request.entity.trim().length < 2) {
      throw new Error('Entity must be at least 2 characters long');
    }

    if (request.depth !== undefined) {
      if (typeof request.depth !== 'number' || request.depth < 1 || request.depth > 5) {
        throw new Error('Depth must be a number between 1 and 5');
      }
    }

    if (request.minInfluenceThreshold !== undefined) {
      if (typeof request.minInfluenceThreshold !== 'number' || 
          request.minInfluenceThreshold < 0 || 
          request.minInfluenceThreshold > 1) {
        throw new Error('Min influence threshold must be a number between 0 and 1');
      }
    }
  }

  /**
   * Validate top influencers request
   */
  private _validateTopInfluencersRequest(request: {
    domain: string;
    limit?: number;
    timeframe?: string;
  }): void {
    if (!request.domain || typeof request.domain !== 'string') {
      throw new Error('Domain is required and must be a string');
    }

    if (request.domain.trim().length < 2) {
      throw new Error('Domain must be at least 2 characters long');
    }

    if (request.limit !== undefined) {
      if (typeof request.limit !== 'number' || request.limit < 1 || request.limit > 100) {
        throw new Error('Limit must be a number between 1 and 100');
      }
    }

    if (request.timeframe !== undefined) {
      const validTimeframes = ['1d', '7d', '30d', '90d', '1y'];
      if (!validTimeframes.includes(request.timeframe)) {
        throw new Error(`Timeframe must be one of: ${validTimeframes.join(', ')}`);
      }
    }
  }
}
